/**
 * ConfigVersionPage — 配置版本管理
 *
 * Manages DynamicConfig publishing, versioning, and rollback.
 * Supports two channels: final (production) and experimental.
 *
 * API:
 *   GET  /config/current            — 当前生效配置
 *   GET  /config/versions            — 版本历史列表
 *   POST /config/publish             — 发布新版本
 *   POST /config/rollback/:version   — 回滚到指定版本
 *   GET  /config/default             — 默认模板
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  RotateCcw,
  Upload,
  Clock,
  CheckCircle,
  Loader2,
  RefreshCw,
  ChevronRight,
  Eye,
  Copy,
  FileJson,
  Layers,
  Shield,
  AlertTriangle,
  Beaker,
  Flag,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────

interface ConfigVersionRow {
  id: string;
  version: number;
  channel: string;
  created_at: string;
}

interface DynamicConfig {
  tenant: string;
  channel: string;
  layoutVersion: string;
  issuedAt: string;
  signature: string;
  menu?: unknown[];
  pages?: unknown[];
  capabilities?: unknown[];
  featureFlags?: Record<string, boolean>;
  [key: string]: unknown;
}

type Channel = 'final' | 'experimental';

// ─── Component ─────────────────────────────────────

export default function ConfigVersionPage() {
  const { t } = useTranslation();

  const [channel, setChannel] = useState<Channel>('final');
  const [versions, setVersions] = useState<ConfigVersionRow[]>([]);
  const [totalVersions, setTotalVersions] = useState(0);
  const [currentConfig, setCurrentConfig] = useState<DynamicConfig | null>(null);
  const [currentVersion, setCurrentVersion] = useState<number | null>(null);
  const [isDefault, setIsDefault] = useState(false);
  const [fromCache, setFromCache] = useState(false);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  const [expandedVersion, setExpandedVersion] = useState<number | null>(null);
  const [previewConfig, setPreviewConfig] = useState<DynamicConfig | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editorContent, setEditorContent] = useState('');

  // ─── Load current config & versions ──────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [currentRes, versionsRes] = await Promise.all([
        api.get<{ config: DynamicConfig; version?: number; fromCache: boolean; isDefault?: boolean }>(
          `/config/current?channel=${channel}`,
        ),
        api.get<{ items: ConfigVersionRow[]; total: number }>(
          `/config/versions?channel=${channel}&limit=50`,
        ),
      ]);
      setCurrentConfig(currentRes.config);
      setCurrentVersion(currentRes.version ?? null);
      setIsDefault(currentRes.isDefault ?? false);
      setFromCache(currentRes.fromCache);
      setVersions(versionsRes.items);
      setTotalVersions(versionsRes.total);
    } catch {
      setCurrentConfig(null);
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [channel]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ─── Publish ─────────────────────────────────────

  const handlePublish = async () => {
    if (!editorContent) return;
    setPublishing(true);
    try {
      const parsedConfig = JSON.parse(editorContent);
      await api.post('/config/publish', {
        config: parsedConfig,
        channel,
      });
      setShowEditor(false);
      setEditorContent('');
      await loadData();
    } catch {
      // parse or API error — stay in editor
    } finally {
      setPublishing(false);
    }
  };

  // ─── Rollback ────────────────────────────────────

  const handleRollback = async (version: number) => {
    setRollingBack(true);
    try {
      await api.post(`/config/rollback/${version}?channel=${channel}`, {});
      await loadData();
    } finally {
      setRollingBack(false);
    }
  };

  // ─── Load default template into editor ───────────

  const loadDefaultTemplate = async () => {
    try {
      const res = await api.get<{ config: DynamicConfig }>('/config/default');
      setEditorContent(JSON.stringify(res.config, null, 2));
      setShowEditor(true);
    } catch {
      /* ignore */
    }
  };

  // ─── Load current config into editor ─────────────

  const editCurrent = () => {
    if (currentConfig) {
      setEditorContent(JSON.stringify(currentConfig, null, 2));
      setShowEditor(true);
    }
  };

  // ─── Copy config ─────────────────────────────────

  const copyConfig = (config: unknown) => {
    navigator.clipboard.writeText(JSON.stringify(config, null, 2)).catch(() => {});
  };

  const fmtDate = (s: string) => {
    try {
      return new Date(s).toLocaleString('zh-CN', { timeZone: 'Asia/Singapore' });
    } catch {
      return s;
    }
  };

  // ─── Render ──────────────────────────────────────

  return (
    <div className="flex flex-col h-full p-4 gap-4">
      {/* Header */}
      <Card>
        <CardContent className="py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Layers className="h-5 w-5 text-primary" />
            <h1 className="font-semibold">{t('admin.configVer.title')}</h1>
            <Badge variant="outline" className="text-xs">{t('admin.configVer.versions', { count: totalVersions })}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadData}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              {t('admin.refresh')}
            </Button>
            <Button variant="outline" size="sm" onClick={loadDefaultTemplate}>
              <FileJson className="h-3.5 w-3.5 mr-1" />
              {t('admin.configVer.defaultTemplate')}
            </Button>
            <Button size="sm" onClick={editCurrent} disabled={!currentConfig}>
              <Upload className="h-3.5 w-3.5 mr-1" />
              {t('admin.configVer.editPublish')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Channel toggle */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setChannel('final')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors',
            channel === 'final' ? 'bg-green-500/10 text-green-700 border border-green-300' : 'hover:bg-muted text-muted-foreground border border-transparent',
          )}
        >
          <Flag className="h-3.5 w-3.5" />
          {t('admin.configVer.finalChannel')} (final)
        </button>
        <button
          onClick={() => setChannel('experimental')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors',
            channel === 'experimental' ? 'bg-amber-500/10 text-amber-700 border border-amber-300' : 'hover:bg-muted text-muted-foreground border border-transparent',
          )}
        >
          <Beaker className="h-3.5 w-3.5" />
          {t('admin.configVer.experimentalChannel')} (experimental)
        </button>
      </div>

      <div className="flex-1 overflow-auto flex gap-4">
        {/* Left: Current Config Summary */}
        <div className="w-96 shrink-0 space-y-3">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('admin.configVer.currentConfig')}</h3>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : currentConfig ? (
            <Card>
              <CardContent className="py-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium">
                      v{currentVersion ?? t('admin.configVer.defaultLabel')}
                    </span>
                    {isDefault && <Badge className="bg-amber-500/10 text-amber-600 text-[10px]">{t('admin.configVer.defaultLabel')}</Badge>}
                    {fromCache && <Badge className="bg-primary/10 text-primary text-[10px]">{t('admin.configVer.cached')}</Badge>}
                  </div>
                  <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => copyConfig(currentConfig)}>
                    <Copy className="h-3 w-3 mr-1" />
                    {t('admin.copy')}
                  </Button>
                </div>

                {/* Config metadata */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <label className="text-[10px] text-muted-foreground">{t('admin.configVer.tenant')}</label>
                    <p className="font-mono text-xs">{currentConfig.tenant}</p>
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground">{t('admin.configVer.layoutVersion')}</label>
                    <p className="font-mono text-xs">{currentConfig.layoutVersion}</p>
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground">{t('admin.configVer.publishTime')}</label>
                    <p className="text-xs">{fmtDate(currentConfig.issuedAt)}</p>
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground">{t('admin.configVer.signature')}</label>
                    <p className="font-mono text-xs truncate" title={currentConfig.signature}>
                      <Shield className="inline h-3 w-3 mr-0.5 text-green-500" />
                      {currentConfig.signature?.slice(0, 12)}...
                    </p>
                  </div>
                </div>

                {/* Config stats */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-muted/30 rounded p-2 text-center">
                    <p className="text-lg font-bold">{Array.isArray(currentConfig.menu) ? currentConfig.menu.length : 0}</p>
                    <p className="text-[10px] text-muted-foreground">{t('admin.configVer.menuItems')}</p>
                  </div>
                  <div className="bg-muted/30 rounded p-2 text-center">
                    <p className="text-lg font-bold">{Array.isArray(currentConfig.pages) ? currentConfig.pages.length : 0}</p>
                    <p className="text-[10px] text-muted-foreground">{t('admin.configVer.pages')}</p>
                  </div>
                  <div className="bg-muted/30 rounded p-2 text-center">
                    <p className="text-lg font-bold">{Object.keys(currentConfig.featureFlags ?? {}).length}</p>
                    <p className="text-[10px] text-muted-foreground">{t('admin.configVer.featureFlags')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-6 text-center text-muted-foreground text-sm">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-30" />
                {t('admin.configVer.noConfig')}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Version history */}
        <div className="flex-1 space-y-3">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('admin.configVer.versionHistory')}</h3>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : versions.length === 0 ? (
            <Card>
              <CardContent className="py-6 text-center text-muted-foreground text-sm">
                <Clock className="h-8 w-8 mx-auto mb-2 opacity-30" />
                {t('admin.configVer.noVersionHistory')}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-1.5">
              {versions.map((v, idx) => (
                <Card key={v.id} className={cn(v.version === currentVersion && 'border-primary/30 bg-primary/5')}>
                  <CardContent className="py-2 px-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button onClick={() => setExpandedVersion(expandedVersion === v.version ? null : v.version)}>
                          <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', expandedVersion === v.version && 'rotate-90')} />
                        </button>
                        <span className="text-sm font-mono font-medium">v{v.version}</span>
                        {idx === 0 && <Badge className="bg-green-500/10 text-green-600 text-[10px]">{t('admin.configVer.latest')}</Badge>}
                        {v.version === currentVersion && <Badge className="bg-primary/10 text-primary text-[10px]">{t('admin.configVer.current')}</Badge>}
                        <span className="text-xs text-muted-foreground ml-2">
                          <Clock className="inline h-3 w-3 mr-0.5" />
                          {fmtDate(v.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        {v.version !== currentVersion && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 text-[10px]"
                            onClick={() => handleRollback(v.version)}
                            disabled={rollingBack}
                          >
                            {rollingBack ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RotateCcw className="h-3 w-3 mr-1" />}
                            {t('admin.configVer.rollbackTo')}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Editor overlay */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-8">
          <Card className="w-full max-w-4xl max-h-[85vh] flex flex-col">
            <CardHeader className="pb-2 flex flex-row items-center justify-between shrink-0">
              <CardTitle className="text-sm">
                {t('admin.configVer.publishNewVersion')} — {channel === 'final' ? t('admin.configVer.finalChannel') : t('admin.configVer.experimentalChannel')}
              </CardTitle>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowEditor(false)}>
                ✕
              </Button>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden flex flex-col gap-3 pb-4">
              <textarea
                value={editorContent}
                onChange={(e) => setEditorContent(e.target.value)}
                className="flex-1 font-mono text-xs bg-muted/30 rounded p-3 resize-none border focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="Paste DynamicConfig JSON ..."
                spellCheck={false}
              />
              <div className="flex justify-between items-center">
                <p className="text-xs text-muted-foreground">
                  {t('admin.configVer.publishNote')}
                </p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setShowEditor(false)}>{t('admin.cancel')}</Button>
                  <Button size="sm" onClick={handlePublish} disabled={publishing || !editorContent}>
                    {publishing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
                    {t('admin.configVer.publish')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
